import { ConfigDiff } from '@hakimo-ui/hakimo/types';
import { useState } from 'react';
import { isRedacted } from './redaction';

const MAX_INLINE = 180;

type Kind = 'changed' | 'added' | 'removed';

function Value(props: { value: unknown; kind: Kind }) {
  const { value, kind } = props;
  const [expanded, setExpanded] = useState(false);
  // Always JSON, so "5" stays distinct from 5 and "" is visible.
  const text = JSON.stringify(value) ?? String(value);
  const long = text.length > MAX_INLINE;

  return (
    <span
      className={
        kind === 'removed'
          ? 'text-red-700 line-through dark:text-red-400'
          : 'text-green-700 dark:text-green-400'
      }
    >
      {long && !expanded ? `${text.slice(0, MAX_INLINE)}…` : text}
      {long && (
        <button
          type="button"
          className="text-primary-600 dark:text-primary-400 ml-1 no-underline"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'less' : `more (${text.length} chars)`}
        </button>
      )}
      {isRedacted(value) && (
        <span
          className="ml-1 text-amber-700 dark:text-amber-400"
          title="Redacted before storage. The real value is not recorded here."
        >
          (redacted)
        </span>
      )}
    </span>
  );
}

interface RowProps {
  path: string;
  kind: Kind;
  from?: unknown;
  to?: unknown;
}

function Row({ path, kind, from, to }: RowProps) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-4 py-1">
      <span className="break-all font-mono text-xs text-gray-600 dark:text-gray-300">
        {path}
      </span>
      <span className="break-all font-mono text-xs">
        {kind !== 'added' && <Value value={from} kind="removed" />}
        {kind === 'changed' && <span className="text-gray-400"> → </span>}
        {kind !== 'removed' && <Value value={to} kind="added" />}
      </span>
    </div>
  );
}

export function DiffBody({ diff }: { diff: ConfigDiff }) {
  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-800">
      {Object.entries(diff.changed).map(([path, { from, to }]) => (
        <Row key={`c:${path}`} path={path} kind="changed" from={from} to={to} />
      ))}
      {Object.entries(diff.added).map(([path, to]) => (
        <Row key={`a:${path}`} path={path} kind="added" to={to} />
      ))}
      {Object.entries(diff.removed).map(([path, from]) => (
        <Row key={`r:${path}`} path={path} kind="removed" from={from} />
      ))}
    </div>
  );
}
